import { createEmitter } from "ts_utils";
import { ValidationUtils } from "ts_utils";
import { Services } from "../services/Services";
import { Templates } from "../ui/Templates";
import { UserAccountDialog } from "../user/ui/UserAccountDialog";
import { UserManager } from "../user/UserManager";
import { TeamUploadDTO } from "../data/dtos";
import { TeamDO } from "../data/TeamDO";
import { TeamLIEvent, TeamListItem } from "./TeamListItem";
import { ConfirmationBox } from "../ui/ConfirmationBox";
import { teamsDg } from "../ui/templates/teamTemplates";

export class TeamsDialog {	
	private _ui:HTMLDivElement;
	
	private _page1:HTMLDivElement;
	private _page2:HTMLDivElement;
	private _page3:HTMLDivElement;
	
	//all pages
	private _messageDiv:HTMLDivElement;
	private _btn1:HTMLButtonElement;
	private _btn2:HTMLButtonElement;
	private _btn3:HTMLButtonElement;
	
	//for page 2
	private _teamsListDiv:HTMLDivElement;
	private _teams:TeamListItem[] = [];
	private _selectedTeam:TeamListItem;
	
	//for page 3
	private _teamNameInput:HTMLInputElement;
	private _teamCodeInput:HTMLInputElement;
	private _teamSizeInput:HTMLInputElement;
	
	private static _userAccountDialog:UserAccountDialog;
	
	private _teamCreated:boolean = false;
	/**true if at least one team created*/
	public get teamCreated():boolean { return this._teamCreated; }
	
	private _teamRemoved:boolean = false;
	/**true if a team is removed*/
	public get teamRemoved():boolean { return this._teamRemoved; }
	
	private _emitter = createEmitter<TeamsDialogEvent>();
	public get event() { return this._emitter.event; }
	
	constructor(){
		this._init();
	}
	
	private _init():void {
		this._draw();
		if (TeamsDialog._userAccountDialog == null){
			TeamsDialog._userAccountDialog = new UserAccountDialog();
		}
		
		this._setPage(1);
	}
	
	private _draw():void {
		this._ui = Templates.createDiv(teamsDg);
		let qs = this._ui.querySelector.bind(this._ui);
		
		this._page1 = qs(".teamsdg-page1");
		this._page2 = qs(".teamsdg-page2");
		this._page3 = qs(".teamsdg-page3");
		
		this._messageDiv = qs(".teamsdg-message");
		this._btn1 = qs(".teamsdg-btn1");
		this._btn2 = qs(".teamsdg-btn2");
		this._btn3 = qs(".teamsdg-btn3");
		
		this._teamsListDiv = qs(".teamsdg-teamsListDiv");
		
		this._teamNameInput = qs(".teamsdg-teamNameInput");
		this._teamCodeInput = qs(".teamsdg-codeInput");
		this._teamSizeInput = qs(".teamsdg-teamSizeInput");
		
		document.body.append(this._ui);
	}
	
	private async _setPage(pageNumber:number):Promise<void> {
		this._removeListeners();
		
		this._page1.style.display = "none";
		this._page2.style.display = "none";
		this._page3.style.display = "none";
		
		this._btn2.style.display = "none";
		
		if (pageNumber === 1){
			this._enablePage1();
		}
		else if (pageNumber === 2){
			await this._enablePage2();
		}
		else if (pageNumber === 3){
			this._enablePage3();
		}
	}
	
	/**////////////////PAGE 1//////////////////*/
	private _enablePage1():void {
		this._page1.style.display = "initial";
		this._btn1.innerHTML = "Sign In";
		this._btn1.addEventListener("click", this._onP1ContinueBtnClick);
		this._btn3.addEventListener("click", this._onCloseBtnClick);
	}
	
	private _onP1ContinueBtnClick = async (e:MouseEvent):Promise<void> => {
		this._disableButtons();
		
		let um = UserManager.get();
		await um.logout();
		if (um.currentUser == null){
			let display:string = this._ui.style.display;
			this._ui.style.display = "none";
			
			await TeamsDialog._userAccountDialog.show();
			this._ui.style.display = display;
		}
		
		this._enableButtons();
		
		if (um.currentUser != null){
			await this._setPage(2);
		}
	}
	
	/**////////////////PAGE 2//////////////////*/
	private async _enablePage2():Promise<void> {
		this._disableButtons();
		
		this._page2.style.display = "initial";
		this._btn2.style.display = "initial";
		this._btn1.innerHTML = "Add Team";
		this._btn2.innerHTML = "Delete Team";
		this._btn3.innerHTML = "Sign Out";
		this._btn1.addEventListener("click", this._onP2AddTeamBtnClick);
		this._btn2.addEventListener("click", this._onP2DeleteTeamBtnClick);
		this._btn3.addEventListener("click", this._onCloseBtnClick);
		
		await this._populateTeams();
		
		this._enableButtons();
	}
	
	private async _populateTeams():Promise<void> {
		let um = UserManager.get();
		let rdto = await Services.getTeamsForUser(um.currentUser.userId);
		if (!rdto.success){
			console.log(rdto.message);
			return;
		}
		
		let teamDOs = rdto.dataList.map(dto => TeamDO.fromTeamDto(dto));
		
		//remove old teams
		this._selectedTeam = null;
		
		for (let li of this._teams){
			li.event.offAll();
			li.prepareForRemoval();
			li.ui.remove();
		}
		this._teams = [];
		
		//add new teams
		
		for (let tdo of teamDOs){
			if (tdo.userId !== um.currentUser.userId){
				continue;
			}
			let li = new TeamListItem(tdo);
			this._teamsListDiv.append(li.ui);
			li.event.on(this._onTeamLIEvent);
			this._teams.push(li);
		}
	}
	
	private _onTeamLIEvent = (e:TeamLIEvent):void => {
		for (let li of this._teams){
			if (li === e.target){
				this._selectedTeam = li;
				li.select();
			}
			else {
				li.deselect();
			}
		}
	}
	
	private _onP2AddTeamBtnClick = (e:MouseEvent):void => {
		this._setPage(3);
	}
	
	private _onP2DeleteTeamBtnClick = async (e:MouseEvent):Promise<void> => {
		if (this._selectedTeam == null){
			return;
		}
		
		let cb = new ConfirmationBox("Permanently delete this team?");
		let confirmed = await cb.awaitConfirmation();
		if (!confirmed){
			return;
		}
		
		this._disableButtons();
		
		await Services.removeTeam(this._selectedTeam.teamDO.id);
		this._teamRemoved = true;
		
		await this._populateTeams();
		this._enableButtons();
	}
	
	/**////////////////PAGE 3//////////////////*/
	private _enablePage3():void {
		this._page3.style.display = "initial";
		this._btn1.innerHTML = "Create Team";
		this._btn3.innerHTML = "Cancel";
		this._teamNameInput.value = "";
		this._teamCodeInput.value = "";
		this._btn1.addEventListener("click", this._onP3CreateTeamBtnClick);
		this._btn3.addEventListener("click", this._onP3CancelBtnClick);
	}
	
	private _onP3CreateTeamBtnClick = async (e:MouseEvent):Promise<void> => {
		if (!this._validateTeamInfo()){
			return;
		}
		this._disableButtons();
		let dto = new TeamUploadDTO();
		dto.name = this._teamNameInput.value;
		dto.code = this._teamCodeInput.value;
		dto.size = Number.parseInt(this._teamSizeInput.value);
		dto.userId = UserManager.get().currentUser.userId;
		let rdto = await Services.addTeam(dto);
		this._enableButtons();
		if (rdto.success){
			this._teamCreated = true;
			this._setPage(2);
		}
		else {
			if (rdto.message === "code taken"){
				this._messageDiv.innerHTML = ("This code is unavailable.");
			}
			else if (rdto.message === "name taken"){
				this._messageDiv.innerHTML = ("This team name is unavailable.");
			}
			this._messageDiv.innerHTML =
				("There was a problem creating this team.");
		}
	}
	
	private _validateTeamInfo():boolean {
		let name:string = this._teamNameInput.value;
		if (name.length < 5){
			this._messageDiv.innerHTML =
				"Team name must be at least 5 characters";
			return false;
		}
		if (!ValidationUtils.isValidDisplayName(name)){
			this._messageDiv.innerHTML = ("Team name can't contain special " +
				"characters");
			return false;
		}
		let code:string = this._teamCodeInput.value;
		if (code.length < 5){
			this._messageDiv.innerHTML =
				"Team code must be at least 5 characters";
			return false;
		}
		
		let regExp = /^[a-zA-Z]+[a-zA-Z0-9]+$/;
		if (!regExp.test(code)){
			this._messageDiv.innerHTML = "Team code must begin with a letter " +
				"and contain only letters and numbers";
			return false;
		}
		
		return true;
	}
	
	private _onP3CancelBtnClick = (e:MouseEvent):void => {
		this._setPage(2);
	}
	
	private _disableButtons():void {
		this._btn1.disabled = true;
		this._btn2.disabled = true;
		this._btn3.disabled = true;
	}
	
	private _enableButtons():void {
		this._btn1.disabled = false;
		this._btn2.disabled = false;
		this._btn3.disabled = false;
	}
	
	private _onCloseBtnClick = (e:MouseEvent):void => {
		this._removeListeners();
		this._ui.remove();
		this._emitter.emit(TeamsDialogEvent.Finished);
	}
	
	private _removeListeners():void {
		this._btn1.removeEventListener("click", this._onP1ContinueBtnClick);
		this._btn3.removeEventListener("click", this._onCloseBtnClick);
		
		this._btn1.removeEventListener("click", this._onP2AddTeamBtnClick);
		this._btn2.removeEventListener("click", this._onP2DeleteTeamBtnClick);
		this._btn3.removeEventListener("click", this._onCloseBtnClick);
		
		this._btn1.removeEventListener("click", this._onP3CreateTeamBtnClick);
		this._btn3.removeEventListener("click", this._onP3CancelBtnClick);
	}
}

export enum TeamsDialogEvent {
	Finished
}